import { IconAlertCircle, IconCheck, IconInfo } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { createDonation } from '../lib/donations';
import { type RunnerRow, useRunners } from '../lib/fleet';
import { createMutation } from '../lib/mutation';
import { ConnectMachineBody } from './ConnectMachineBody';
import { ErrorState } from './ErrorState';
import { Field } from './Field';
import { Select } from './Select';
import { Button } from './ui/Button';

// Step 3 of the donate wizard (arc 101 slice 11, plan doc 05): pin a box.
//
// Two halves, because enrolling a box and DONATING one are different acts. The
// gift needs a `runner_id`, and neither enrollment transport can hand one back:
// the token path reveals a credential the operator carries to the box, and the
// SSH path answers 202 for a bootstrap that runs out of band. So the donor picks
// from the boxes they already have, and <ConnectMachineBody/> — the same body
// the fleet's connect-a-machine dialog mounts — is here for the case where they
// have none yet. They come back and pin it once it joins.

export type DonateBoxStepProps = {
  readonly project: string;
  readonly token: string | null;
  /** The wizard's open state — the enroll panels re-arm on close, never on unmount. */
  readonly open: boolean;
  /** A live `compute` gift is already pinned to this project. */
  readonly done: boolean;
  readonly onDonated: () => void;
};

export function DonateBoxStep(props: DonateBoxStepProps): JSX.Element {
  const runners = useRunners(() => null, props.token);
  const [picked, setPicked] = createSignal('');
  const [enrolling, setEnrolling] = createSignal(false);

  // Page one of the roster; `useRunners` already drops revoked boxes. An offline
  // one is still offered — it is enrolled, so it can hold an earmark, and a box
  // that reboots after being pinned is the normal case.
  const choices = (): readonly RunnerRow[] => runners.data?.data ?? [];

  // A 402 is the paywall's own sentence, a 422 the target/resource refusal — both
  // already say what to fix, so the fallback covers only a failure carrying none.
  const donate = createMutation({
    call: (runnerId: string) =>
      createDonation(
        { kind: 'compute', target: 'project', repo_full_name: props.project, runner_id: runnerId },
        props.token,
      ),
    fallback: t('donations.box_error'),
    onSuccess: () => {
      setPicked('');
      props.onDonated();
    },
  });
  const busy = donate.pending;
  const error = donate.error;

  const give = (): void => {
    const runnerId = picked();
    if (runnerId === '') return;
    void donate.run(runnerId);
  };

  return (
    <div class="field-group">
      <Show when={props.done}>
        <div class="alert alert--icon" role="status">
          <IconCheck />
          <p class="alert__title">{t('donations.box_done')}</p>
        </div>
      </Show>

      <p class="field__desc">{t('donations.box_intro')}</p>
      <div class="alert alert--icon" role="note">
        <IconInfo />
        <p class="alert__title">{t('donations.box_fence')}</p>
      </div>

      <Field
        label={t('donations.box_pick_label')}
        id="donate-box"
        desc={
          !runners.isError && !runners.isLoading && choices().length === 0
            ? t('donations.box_empty')
            : undefined
        }
      >
        {(a11y) => (
          <>
            <Select
              a11y={a11y}
              label={t('donations.box_pick_label')}
              block
              value={picked()}
              options={[
                { value: '', label: t('donations.box_pick_none') },
                ...choices().map((runner) => ({
                  value: runner.id,
                  label: runner.hostname ?? runner.id,
                })),
              ]}
              onChange={setPicked}
            />
            {/* A failed roster read is NOT an empty fleet. "Enroll one below" would
                send a donor who already has boxes off to enrol a duplicate over an
                expired session or a blip, so the failure says so and offers the
                retry — `box_empty` stays the answer only when the read succeeded. */}
            <Show when={runners.isError}>
              <ErrorState error={runners.error} onRetry={() => void runners.refetch()} />
            </Show>
          </>
        )}
      </Field>

      <Show when={error()}>
        <div class="alert alert--icon alert--destructive" role="alert">
          <IconAlertCircle />
          <p class="alert__title">{error()}</p>
        </div>
      </Show>

      <div class="dialog__footer">
        <Button disabled={busy() || picked() === ''} onClick={give}>
          {t(busy() ? 'donations.box_giving' : 'donations.box_give')}
        </Button>
      </div>

      {/* Disclosure, not a swap: the enroll body stays MOUNTED once opened and is
          hidden by attribute, for the same reason its own two panels are — a
          revealed one-time token whose seat slot is already burnt must survive
          the donor looking away. */}
      <Button
        variant="outline"
        size="sm"
        aria-expanded={enrolling()}
        aria-controls="donate-box-enroll"
        onClick={() => setEnrolling((on) => !on)}
      >
        {t('donations.box_enroll_heading')}
      </Button>
      <div id="donate-box-enroll" hidden={!enrolling()}>
        <ConnectMachineBody
          open={props.open}
          token={props.token}
          idPrefix="donate-box"
          onEnrolled={() => void runners.refetch()}
          onClose={() => setEnrolling(false)}
        />
      </div>
    </div>
  );
}
